import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { format } from "date-fns";
import { Tickets, ChevronLeft, ChevronRight, Printer, Save, Plus, Trash2, ArrowUpDown } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DateWithDataPicker } from "@/components/DateWithDataPicker";
import { ExportButton } from "@/components/ExportButton";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PrintHeader from "@/components/PrintHeader";
import { useFlushOnHide, keepaliveSave } from "@/hooks/useFlushOnHide";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VENDOR_SUGGESTIONS = ["XYZ", "National Tobacco Co.", "Phillip Morris", "Swedish Match", "RJ Reynolds", "Altria", "ITG Brands"];

const toISO = (d) => format(d, "yyyy-MM-dd");
const num = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const money = (n) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Coupon() {
  const { user } = useAuth();
  const today = new Date();
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [couponDataRefreshKey, setCouponDataRefreshKey] = useState(0);
  // Per-vendor row selection: { [colIdx]: Set<rowIdx> }
  const [selected, setSelected] = useState({});
  const dirtyTimer = useRef(null);
  const dirtyRef = useRef(false);
  const latestSheetRef = useRef(null);
  const autoSaveRef = useRef(() => {});
  const dateIso = toISO(date);
  useEffect(() => { latestSheetRef.current = data; }, [data]);

  // Move cursor down one row in the same column (qty or value) on Enter.
  const handleEnterDown = (colIdx, rIdx, field) => (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.currentTarget.blur();
    const next = document.querySelector(`[data-testid="coupon-${colIdx}-row-${rIdx + 1}-${field}"]`);
    if (next) next.focus();
  };

  // Toggle row selection (supports shift-click range)
  const toggleRowSelect = (colIdx, rIdx, shiftKey) => {
    setSelected((prev) => {
      const cur = new Set(prev[colIdx] || []);
      if (shiftKey && cur.size > 0) {
        const sortedExisting = [...cur].sort((a, b) => a - b);
        const anchor = sortedExisting[0];
        const [from, to] = anchor < rIdx ? [anchor, rIdx] : [rIdx, anchor];
        for (let i = from; i <= to; i += 1) cur.add(i);
      } else if (cur.has(rIdx)) {
        cur.delete(rIdx);
      } else {
        cur.add(rIdx);
      }
      return { ...prev, [colIdx]: cur };
    });
  };

  const clearSelectedRows = (colIdx) => {
    const rowSet = selected[colIdx];
    if (!rowSet || rowSet.size === 0) return;
    setData((prev) => {
      const cols = prev.columns.map((c, i) => {
        if (i !== colIdx) return c;
        return {
          ...c,
          rows: c.rows.map((r, ri) => (rowSet.has(ri) ? { qty: "", value: "" } : r)),
        };
      });
      return { ...prev, columns: cols };
    });
    setSelected((prev) => ({ ...prev, [colIdx]: new Set() }));
  };

  const fetchSheet = useCallback(async (iso) => {
    try {
      const res = await axios.get(`${API}/coupons/${iso}`);
      let sheet = res.data;
      // Draft restore: prefer a local draft newer than the server copy
      try {
        const raw = localStorage.getItem(`coupon_draft_${iso}`);
        if (raw) {
          const draft = JSON.parse(raw);
          const serverTs = sheet.updated_at ? Date.parse(sheet.updated_at) : 0;
          if (draft?.date === iso && Number(draft.ts) > serverTs && draft.sheet) {
            sheet = draft.sheet;
            toast.message("Restored unsaved coupon changes from your last session — saving now…");
            autoSaveRef.current(draft.sheet);
          } else {
            localStorage.removeItem(`coupon_draft_${iso}`);
          }
        }
      } catch { /* localStorage unavailable — ignore */ }
      setData(sheet);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load coupon sheet");
    }
  }, []);

  // Pull the user's business name once for the in-column banner
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/user/profile`).then(res => {
      if (!cancelled) setProfile(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const businessName = (profile?.company_name || profile?.dba_name || profile?.store_name || user?.name || "Your Company").toUpperCase();
  const businessEin = (profile?.ein || "").trim();

  useEffect(() => { fetchSheet(dateIso); }, [dateIso, fetchSheet]);

  const persist = useCallback(async (next) => {
    setSaving(true);
    try {
      await axios.put(`${API}/coupons/${dateIso}`, next);
      dirtyRef.current = false;
      // Server confirmed — safe to drop the local draft.
      try { localStorage.removeItem(`coupon_draft_${dateIso}`); } catch { /* ignore */ }
      // Bump so the date picker refreshes its "has data" highlight.
      setCouponDataRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [dateIso]);

  const autoSave = useCallback((next) => {
    dirtyRef.current = true;
    // Draft to localStorage on every edit — restored on next load if newer
    // than the server copy (survives tab crash / suspension).
    try {
      localStorage.setItem(`coupon_draft_${dateIso}`, JSON.stringify({ ts: Date.now(), date: dateIso, sheet: next }));
    } catch { /* localStorage full or unavailable — silent */ }
    if (dirtyTimer.current) clearTimeout(dirtyTimer.current);
    dirtyTimer.current = setTimeout(() => persist(next), 800);
  }, [persist, dateIso]);
  autoSaveRef.current = autoSave;

  // Flush pending edits via keepalive fetch when the tab is hidden/unloaded —
  // background tabs throttle setTimeout so the debounced save may never fire.
  const flushRef = useRef(null);
  flushRef.current = () => {
    if (!dirtyRef.current || !latestSheetRef.current) return;
    if (dirtyTimer.current) { clearTimeout(dirtyTimer.current); dirtyTimer.current = null; }
    keepaliveSave(`${API}/coupons/${dateIso}`, latestSheetRef.current);
    dirtyRef.current = false;
  };
  useFlushOnHide(flushRef);

  const updateColumn = (idx, patch) => {
    setData(prev => {
      const next = { ...prev, columns: prev.columns.map((c, i) => i === idx ? { ...c, ...patch } : c) };
      autoSave(next);
      return next;
    });
  };

  const updateRow = (colIdx, rowIdx, field, val) => {
    setData(prev => {
      const cols = prev.columns.map((c, i) => {
        if (i !== colIdx) return c;
        const rows = c.rows.map((r, j) => j === rowIdx ? { ...r, [field]: val } : r);
        return { ...c, rows };
      });
      const next = { ...prev, columns: cols };
      autoSave(next);
      return next;
    });
  };

  const addColumn = () => {
    setData(prev => {
      const next = { ...prev, columns: [...prev.columns, {
        id: crypto.randomUUID(),
        vendor: "",
        inv_date: "",
        received_date: "",
        received_check_no: "",
        received_amount: "",
        postage: "",
        rows: Array.from({ length: 10 }, () => ({ qty: "", value: "" })),
      }] };
      autoSave(next);
      return next;
    });
  };

  const removeColumn = (idx) => {
    if (!confirm("Delete this vendor column? Its rows will be lost.")) return;
    setData(prev => {
      const next = { ...prev, columns: prev.columns.filter((_, i) => i !== idx) };
      autoSave(next);
      return next;
    });
  };

  // Compute per-column totals
  const colTotals = useMemo(() => {
    if (!data) return [];
    return data.columns.map(c => {
      let qty = 0, face = 0;
      c.rows.forEach(r => {
        const q = num(r.qty); const v = num(r.value);
        if (q && v) { qty += q; face += q * v; }
      });
      const sh = (qty * (data.sh_rate_cents || 8)) / 100;
      const postage = num(c.postage);
      const totalReceivable = face + sh + postage;
      return { qty, face, sh, postage, totalReceivable };
    });
  }, [data]);

  // Sort rows within EACH vendor column by Value ascending (smallest first).
  // Empty rows are moved to the bottom so the user can still type into them.
  const sortByFaceAsc = () => {
    if (!data) return;
    const sortedCols = data.columns.map(col => {
      const rows = [...col.rows];
      rows.sort((a, b) => {
        const av = num(a.value);
        const bv = num(b.value);
        const aEmpty = !av;
        const bEmpty = !bv;
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        return av - bv;
      });
      return { ...col, rows };
    });
    setData({ ...data, columns: sortedCols });
  };

  if (!data) {
    return <div className="text-center py-12 text-muted-foreground font-mono">Loading coupon ledger...</div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      {/* Controls bar */}
      <Card className="border-0 shadow-lg print:hidden">
        <CardHeader className="py-3">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <CardTitle className="font-mono flex items-center gap-2">
              <Tickets className="w-5 h-5 text-primary" />
              Coupon Ledger
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => { const d = new Date(date); d.setDate(d.getDate() - 1); setDate(d); }} data-testid="coupon-prev-day">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <DateWithDataPicker
                endpoint="/coupons/dates/range"
                selectedDate={date}
                onSelect={setDate}
                testId="coupon-date"
                refreshKey={couponDataRefreshKey}
              />
              <Button variant="outline" size="sm" onClick={() => { const d = new Date(date); d.setDate(d.getDate() + 1); setDate(d); }} data-testid="coupon-next-day">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDate(new Date())} data-testid="coupon-today">Today</Button>
              <Button variant="outline" size="sm" onClick={addColumn} data-testid="coupon-add-col">
                <Plus className="w-4 h-4 mr-1" /> Add Vendor
              </Button>
              <Button variant="outline" size="sm" onClick={sortByFaceAsc} data-testid="coupon-sort-face">
                <ArrowUpDown className="w-4 h-4 mr-1" /> Sort
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="coupon-print">
                <Printer className="w-4 h-4 mr-1" /> Print
              </Button>
              <ExportButton section="coupons" currentDate={date} testIdPrefix="coupon-export" />
              <Button size="sm" onClick={() => persist(data)} disabled={saving} className="btn-primary" data-testid="coupon-save">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Print area */}
      <div className="coupon-page" data-testid="coupon-sheet">

        {/* Top row: Received Date/CheckNo/Amount per column */}
        <div className="coupon-grid">
          <div className="coupon-sidebar" />
          {data.columns.map((col, idx) => {
            const isLast = idx === data.columns.length - 1;
            const breakAfter = !isLast && (idx + 1) % 3 === 0;
            return (
            <div
              key={col.id}
              className={`coupon-col${breakAfter ? ' cc-page-break' : ''}`}
              data-testid={`coupon-col-${idx}`}
            >
              {/* Per-column business header — visible on print only, so each
                  vendor sheet carries the business info when torn off / mailed */}
              <div className="cc-print-biz">
                <PrintHeader alwaysVisible={true} />
              </div>

              {/* Received header */}
              <div className="cc-received">
                <div className="cc-rec-labels">
                  <span>Date</span>
                  <span>CheckNo</span>
                  <span>Amount</span>
                </div>
                <div className="cc-rec-inputs">
                  <Input
                    value={col.received_date}
                    onChange={(e) => updateColumn(idx, { received_date: e.target.value })}
                    placeholder=""
                    className="cc-rec-input"
                    data-testid={`coupon-${idx}-rec-date`}
                  />
                  <Input
                    value={col.received_check_no}
                    onChange={(e) => updateColumn(idx, { received_check_no: e.target.value })}
                    placeholder=""
                    className="cc-rec-input"
                    data-testid={`coupon-${idx}-rec-check`}
                  />
                  <Input
                    value={col.received_amount}
                    onChange={(e) => updateColumn(idx, { received_amount: e.target.value })}
                    placeholder=""
                    className="cc-rec-input"
                    data-testid={`coupon-${idx}-rec-amount`}
                  />
                </div>
              </div>

              {/* Company name banner — user's business name (not the vendor) */}
              <div className="cc-company">
                <div>{businessName}</div>
                {businessEin && <div className="cc-company-ein">EIN: {businessEin}</div>}
              </div>

              {/* Qty / Value / Total header */}
              <div className="cc-qvt-header">
                <span>Qty.</span><span>Value</span><span>Total</span>
              </div>

              {/* Rows */}
              <div className="cc-rows">
                {col.rows.map((row, rIdx) => {
                  const q = num(row.qty);
                  const v = num(row.value);
                  const t = q && v ? q * v : 0;
                  const isSelected = selected[idx]?.has(rIdx);
                  return (
                    <div
                      key={rIdx}
                      className={`cc-row${isSelected ? ' cc-row-selected' : ''}`}
                      onClick={(e) => {
                        // Only toggle when clicking the row chrome, not an input/button.
                        if (e.target.tagName === 'INPUT' || e.target.closest('button')) return;
                        toggleRowSelect(idx, rIdx, e.shiftKey);
                      }}
                      title="Click row to select. Shift+Click to range-select."
                    >
                      <Input
                        value={row.qty}
                        onChange={(e) => updateRow(idx, rIdx, "qty", e.target.value)}
                        onKeyDown={handleEnterDown(idx, rIdx, "qty")}
                        className="cc-qty"
                        placeholder=""
                        inputMode="numeric"
                        data-testid={`coupon-${idx}-row-${rIdx}-qty`}
                      />
                      <Input
                        value={row.value}
                        onChange={(e) => updateRow(idx, rIdx, "value", e.target.value)}
                        onFocus={(e) => {
                          const raw = num(e.target.value);
                          if (raw) updateRow(idx, rIdx, "value", String(raw));
                        }}
                        onBlur={(e) => {
                          const raw = num(e.target.value);
                          updateRow(idx, rIdx, "value", raw ? money(raw) : "");
                        }}
                        onKeyDown={handleEnterDown(idx, rIdx, "value")}
                        className="cc-value"
                        placeholder=""
                        inputMode="decimal"
                        data-testid={`coupon-${idx}-row-${rIdx}-value`}
                      />
                      <span className="cc-total">{t ? money(t) : ""}</span>
                    </div>
                  );
                })}
              </div>

              {/* Clear selected rows action (only visible when 1+ rows selected) */}
              {selected[idx]?.size > 0 && (
                <button
                  type="button"
                  className="cc-clear-selected"
                  onClick={() => clearSelectedRows(idx)}
                  data-testid={`coupon-${idx}-clear-selected`}
                >
                  Clear {selected[idx].size} selected row{selected[idx].size > 1 ? 's' : ''}
                </button>
              )}

              {/* Footer summary */}
              <div className="cc-footer">
                <div className="cc-fr">
                  <span>Total Face Value</span>
                  <span className="cc-fr-val">{colTotals[idx]?.face ? money(colTotals[idx].face) : ""}</span>
                </div>
                <div className="cc-fr">
                  <span>Add S&amp;H  {data.sh_rate_cents || 8} c /each</span>
                  <span className="cc-fr-val cc-blue">{colTotals[idx]?.sh ? money(colTotals[idx].sh) : "—"}</span>
                </div>
                <div className="cc-fr">
                  <span>Add Postage (aprox)</span>
                  <Input
                    value={col.postage}
                    onChange={(e) => updateColumn(idx, { postage: e.target.value })}
                    className="cc-postage-input"
                    placeholder=""
                    data-testid={`coupon-${idx}-postage`}
                  />
                </div>
                <div className="cc-fr cc-fr-total">
                  <span>Total Receivable</span>
                  <span className="cc-fr-val cc-orange">{colTotals[idx]?.totalReceivable ? money(colTotals[idx].totalReceivable) : ""}</span>
                </div>
                <div className="cc-fr cc-fr-qty">
                  <span>Cupon qty.</span>
                  <span className="cc-fr-val">{colTotals[idx]?.qty || ""}</span>
                </div>
              </div>

              {/* Bottom dark teal area */}
              <div className="cc-teal">
                <div className="cc-inv-row">
                  <span className="cc-inv-label">Inv. Date</span>
                  <Input
                    value={col.inv_date}
                    onChange={(e) => updateColumn(idx, { inv_date: e.target.value })}
                    placeholder=""
                    className="cc-inv-input"
                    data-testid={`coupon-${idx}-inv-date`}
                  />
                </div>
                <Input
                  value={col.vendor}
                  onChange={(e) => updateColumn(idx, { vendor: e.target.value })}
                  className="cc-vendor"
                  list={`vendor-suggest-${idx}`}
                  placeholder="Vendor"
                  data-testid={`coupon-${idx}-vendor`}
                />
                <datalist id={`vendor-suggest-${idx}`}>
                  {VENDOR_SUGGESTIONS.map(v => <option key={v} value={v} />)}
                </datalist>
                <div className="cc-msg">
                  Please Send check for the amount of
                </div>
                <div className="cc-msg-amount">
                  {colTotals[idx]?.totalReceivable ? money(colTotals[idx].totalReceivable) : "_______"}
                  <span> to the address printed</span>
                </div>
                <div className="cc-msg">on the top of this sheet  <em>Thanks</em></div>
                <div className="cc-manager">Manager</div>
                <div className="cc-manager-line"></div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeColumn(idx)}
                  className="cc-remove print:hidden"
                  data-testid={`coupon-${idx}-remove`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Remove
                </Button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .coupon-page {
          background: #134a89;
          padding: 8px 6px;
          border-radius: 8px;
          overflow-x: auto;
        }
        .coupon-grid {
          display: flex;
          gap: 18px;
          min-width: max-content;
        }
        .coupon-sidebar {
          width: 16px;
          flex-shrink: 0;
        }

        /* Compact business info that appears at the top of each vendor
           column when printed — hidden on screen to keep the UI clean. */
        .cc-print-biz { display: none; }
        @media print {
          .cc-print-biz { display: block; background: white; padding: 4px 4px 0; }
          .cc-print-biz .print-header { padding: 0 0 4px; margin-bottom: 0; border-bottom: 1px solid #000; }
          .cc-print-biz .ph-company { font-size: 11px; }
          .cc-print-biz .ph-dba, .cc-print-biz .ph-line { font-size: 9px; }
        }
        .coupon-col {
          width: 215px;
          flex-shrink: 0;
          background: #d8d8d8;
          font-family: 'Courier New', monospace;
          color: #000;
          display: flex;
          flex-direction: column;
          border: 1px solid #0d3a6e;
        }

        /* Received block */
        .cc-received { background: #134a89; padding: 4px; }
        .cc-rec-labels { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 2px; color: #fff; font-size: 11px; font-weight: 600; text-align: center; padding-bottom: 2px; }
        .cc-rec-inputs { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 2px; }
        .cc-rec-input { height: 20px; padding: 0 3px !important; background: white !important; color: #000 !important; font-size: 10px !important; border: 1px solid #888 !important; border-radius: 0 !important; }

        /* Company banner */
        .cc-company {
          background: #d8d8d8;
          color: #0d3a6e;
          text-align: center;
          font-weight: 700;
          padding: 6px 4px;
          font-size: 12px;
          letter-spacing: 0.5px;
          border-top: 1px solid #0d3a6e;
          border-bottom: 1px solid #0d3a6e;
        }
        .cc-company-ein {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.3px;
          margin-top: 2px;
        }

        /* Q/V/T header */
        .cc-qvt-header {
          background: #b8b8b8;
          display: grid;
          grid-template-columns: 1fr 1.2fr 1.1fr;
          padding: 3px 0;
          font-size: 11px;
          font-weight: 700;
          text-align: center;
          border-bottom: 1px solid #0d3a6e;
        }

        /* Rows */
        .cc-rows { background: #fff; border-top: 2px solid #000; }
        .cc-row {
          display: grid;
          grid-template-columns: 1fr 1.2fr 1.1fr;
          align-items: stretch;
          gap: 0;
          height: 26px;
          cursor: pointer;
          position: relative;
        }
        .cc-row.cc-row-selected {
          outline: 2px solid #f59e0b;
          outline-offset: -2px;
          box-shadow: inset 0 0 0 9999px rgba(245, 158, 11, 0.15);
        }
        .cc-clear-selected {
          display: block;
          width: calc(100% - 8px);
          margin: 4px;
          padding: 4px 8px;
          background: #b91c1c;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          border: 1px solid #7f1d1d;
          border-radius: 4px;
          cursor: pointer;
          text-align: center;
        }
        .cc-clear-selected:hover { background: #991b1b; }
        .cc-qty {
          height: 26px !important;
          background: #c8ecd1 !important;
          color: #000 !important;
          text-align: center !important;
          padding: 0 2px !important;
          border-radius: 0 !important;
          border: none !important;
          border-bottom: 1.5px solid #000 !important;
          border-right: 1.5px solid #000 !important;
          font-size: 12px !important;
          box-shadow: none !important;
        }
        .cc-value {
          height: 26px !important;
          background: #fffac5 !important;
          color: #000 !important;
          text-align: right !important;
          padding: 0 3px !important;
          border-radius: 0 !important;
          border: none !important;
          border-bottom: 1.5px solid #000 !important;
          border-right: 1.5px solid #000 !important;
          font-size: 12px !important;
          box-shadow: none !important;
        }
        .cc-total {
          background: #d2d2d2;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 0 4px;
          font-size: 12px;
          color: #000;
          font-weight: 600;
          border-bottom: 1.5px solid #000;
        }

        /* Footer */
        .cc-footer { background: #d8d8d8; padding: 3px; border-top: 1px solid #0d3a6e; }
        .cc-fr { display: flex; justify-content: space-between; align-items: center; gap: 4px; font-size: 11px; padding: 1px 2px; border-bottom: 1px dotted #888; }
        .cc-fr-val { font-weight: 700; min-width: 64px; text-align: right; padding: 1px 4px; background: #f7f7f7; }
        .cc-fr.cc-fr-total { padding: 2px; }
        .cc-fr.cc-fr-total .cc-fr-val { background: #ffd9a3; color: #000; font-weight: 800; padding: 2px 6px; }
        .cc-blue { color: #1955b3; }
        .cc-orange { background: #ffd9a3 !important; }
        .cc-postage-input { height: 16px !important; width: 64px !important; padding: 0 4px !important; font-size: 11px !important; text-align: right; background: #f7f7f7 !important; color: #000 !important; border: 1px solid #aaa !important; border-radius: 0 !important; }

        /* Teal bottom */
        .cc-teal { background: #138282; color: #fff; padding: 6px 6px 12px; font-size: 10px; font-family: 'Courier New', monospace; }
        .cc-inv-row { display: flex; align-items: center; gap: 4px; margin-bottom: 4px; }
        .cc-inv-label { color: #2eff5a; font-style: italic; min-width: 50px; }
        .cc-inv-input { height: 16px !important; padding: 0 4px !important; background: white !important; color: #000 !important; border: 1px solid #fff !important; border-radius: 0 !important; font-size: 10px !important; }
        .cc-vendor {
          height: 28px !important;
          background: #c8ecd1 !important;
          color: #000 !important;
          text-align: center !important;
          text-align-last: center !important;
          font-weight: 700 !important;
          font-size: 12px !important;
          margin: 0 16px 6px !important;
          padding: 0 !important;
          border: 1px solid #000 !important;
          border-radius: 0 !important;
          line-height: 28px !important;
          display: block !important;
          width: calc(100% - 32px) !important;
        }
        .cc-vendor::placeholder {
          text-align: center !important;
          color: #4a6b56 !important;
        }
        .cc-msg { text-align: center; line-height: 1.4; color: rgba(255,255,255,0.85); }
        .cc-msg em { color: #b0ffd2; font-style: italic; }
        .cc-msg-amount { text-align: center; margin: 2px 0; color: #2eff8a; font-weight: 700; }
        .cc-manager { text-align: center; margin-top: 6px; font-weight: 700; color: #fff; }
        .cc-manager-line { border-bottom: 1px solid rgba(255,255,255,0.5); margin: 0 22px; height: 8px; }
        .cc-remove { color: #fcc; margin-top: 6px; width: 100%; }

        @media print {
          @page { size: letter landscape; margin: 0.3in; }
          body { background: white !important; }
          nav, header, footer, .print\\:hidden { display: none !important; }
          .coupon-page { background: white; padding: 0; overflow: visible; }
          .coupon-sidebar { display: none !important; }
          /* Print exactly 3 vendor columns per page */
          .coupon-grid {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 0 !important;
            min-width: 0 !important;
            width: 100% !important;
          }
          .coupon-col {
            width: calc(33.333% - 8px) !important;
            margin: 0 4px 0 4px !important;
            box-sizing: border-box !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Force a page break after every 3rd vendor column (index-based, sidebar-safe).
             The JS render skips this class on the very last column to avoid a trailing blank page. */
          .coupon-col.cc-page-break {
            page-break-after: always !important;
            break-after: page !important;
          }
        }
      `}</style>
    </motion.div>
  );
}
